/**
 * IndicatorGroup.jsx
 * Groups related indicators under a common title
 * Migration from front/src/components/IndicatorGroup.js to React 19 + Modern architecture
 */
import React, { memo } from 'react';
import { Box } from '@mui/material';
import EntityContentTitle from './EntityContentTitle';
import Indicator from './Indicator';

/**
 * Groups related indicators under a common title
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity data (commune/EPCI)
 * @param {Object} props.itemsState - State of all items (selected/unselected)
 * @param {string} props.screenType - Screen type for responsive display
 * @param {string} props.groupTitle - Title for the indicator group
 * @param {Array} props.items - Array of indicator items in this group
 * @param {string} props.anchor - Anchor ID for navigation
 * @returns {React.ReactElement|null} IndicatorGroup component or null if no indicators to show
 */
const IndicatorGroup = ({
  entity = {},
  itemsState,
  screenType,
  groupTitle,
  items = [],
  anchor,
}) => {
  // Check if all indicators for current entity are hidden
  if (items.every(item => !itemsState[item.key])) {
    // All indicators for current entity are hidden: do not render group
    return null;
  }

  return (
    <Box sx={{ pageBreakInside: 'unset' }}>
      <EntityContentTitle title={groupTitle} type={entity.type} id={anchor} />

      {items.map(({ key: indicatorKey }) => {
        const display = itemsState[indicatorKey];

        return (
          <Indicator
            entity={entity}
            key={indicatorKey}
            indicatorKey={indicatorKey}
            display={display}
            screenType={screenType}
          />
        );
      })}
    </Box>
  );
};

IndicatorGroup.displayName = 'IndicatorGroup';

export default memo(IndicatorGroup);
